import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Loader2, MapPin, Send, UserCheck, Archive, ShieldCheck, FolderPlus, CheckCircle2, Copy, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminModalOverlay, AdminModalCard, AdminModalBody } from "@/components/AdminModal";

const ROUTING_LABELS = {
  AUTO_ASSIGNED: "Affecté automatiquement", WAITING_PARTNER: "En attente du partenaire",
  ACCEPTED: "Accepté", PARTNER_REFUSED: "Refusé par le partenaire", UNCOVERED: "Secteur non couvert",
  CENTRAL_REVIEW: "À réaffecter (Centrale)", PROPOSED_TO_NEIGHBOR: "Proposé à un partenaire voisin",
  NEIGHBOR_ACCEPTED: "Accepté (partenaire voisin)", ARCHIVED: "Archivé",
};
const ROUTING_CLS = {
  AUTO_ASSIGNED: "bg-sky-100 text-sky-700", WAITING_PARTNER: "bg-sky-100 text-sky-700",
  ACCEPTED: "bg-sage/20 text-navy", PARTNER_REFUSED: "bg-red-100 text-red-600", UNCOVERED: "bg-orange-100 text-orange-700",
  CENTRAL_REVIEW: "bg-amber-100 text-amber-700", PROPOSED_TO_NEIGHBOR: "bg-indigo-100 text-indigo-700",
  NEIGHBOR_ACCEPTED: "bg-sage/20 text-navy", ARCHIVED: "bg-slate-100 text-slate-500",
};
const FILTERS = [
  ["Non couverts", "UNCOVERED_QUEUE"], ["Tous", null], ["En attente partenaire", "WAITING_PARTNER"],
  ["Acceptés", "ACCEPTED"], ["Refusés", "PARTNER_REFUSED"], ["SLA dépassé", "SLA_EXPIRED"],
  ["Gagnés", "GAGNE"], ["Perdus", "PERDU"], ["Convertis en clients", "CONVERTED"],
];
const UNCOVERED_STATUSES = ["UNCOVERED", "CENTRAL_REVIEW", "PROPOSED_TO_NEIGHBOR"];
const ACCEPTED_STATUSES = ["ACCEPTED", "NEIGHBOR_ACCEPTED"];
const ORIGIN_LABELS = {
  SITE_ESTIMATION: "Estimation en ligne", SITE_CONTACT: "Formulaire de contact", CALLBACK_REQUEST: "Demande de rappel",
  MANUAL_CENTRALE: "Créé par la Centrale", PARTNER_CREATED: "Apporté par un partenaire",
};
const PIPELINE_LABELS = { NOUVEAU: "Nouveau", ATTRIBUE: "Attribué", CONTACTE: "Contacté", RDV: "RDV", PROPOSITION_ENVOYEE: "Proposition envoyée", GAGNE: "Gagné", PERDU: "Perdu" };
const HISTORY_LABELS = {
  assignation_initiale: "Affecté au partenaire du secteur", propose: "Proposé à un partenaire limitrophe",
  affecter_direct: "Affecté directement par la Centrale", conserver_centrale: "Conservé à la Centrale",
  archiver: "Archivé", partner_accept: "Accepté par le partenaire", partner_refuse: "Refusé par le partenaire",
  sla_expiree: "Délai de 24h ouvrées expiré (sans réponse)", cree_par_partenaire: "Créé directement par le partenaire",
  nouvelle_interaction: "Nouvelle interaction (même personne)", tentative_doublon: "Tentative de doublon détectée",
  demande_mise_en_relation: "Demande de mise en relation signalée", pipeline_stage_change: "Étape commerciale modifiée",
  converti_en_client: "Converti en client",
};
const HISTORY_CLS = {
  assignation_initiale: "bg-sky-100 text-sky-700", propose: "bg-indigo-100 text-indigo-700",
  affecter_direct: "bg-sky-100 text-sky-700", conserver_centrale: "bg-amber-100 text-amber-700",
  archiver: "bg-slate-100 text-slate-500", partner_accept: "bg-sage/20 text-navy",
  partner_refuse: "bg-red-100 text-red-600", sla_expiree: "bg-red-100 text-red-600",
  cree_par_partenaire: "bg-sage/20 text-navy", nouvelle_interaction: "bg-sky-100 text-sky-700",
  tentative_doublon: "bg-orange-100 text-orange-700", demande_mise_en_relation: "bg-orange-100 text-orange-700",
  pipeline_stage_change: "bg-indigo-100 text-indigo-700", converti_en_client: "bg-sage/20 text-navy",
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const ProspectHistoryPanel = ({ history, partners }) => {
  if (!history || history.length === 0) return null;
  const partnerName = (id) => {
    const p = partners.find((x) => x.id === id);
    return p ? `${p.firstname} ${p.lastname}` : "—";
  };
  return (
    <div data-testid="prospect-history" className="mt-5">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Historique des propositions</p>
      <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto">
        {[...history].reverse().map((h, i) => (
          <div key={i} data-testid={`prospect-history-row-${i}`}
            className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-1.5 text-xs">
            <span className={`rounded-full px-2 py-0.5 font-bold ${HISTORY_CLS[h.action] || "bg-slate-100 text-slate-500"}`}>
              {HISTORY_LABELS[h.action] || h.action}
            </span>
            <span className="flex-1 text-right text-slate-500">{h.partner_id ? partnerName(h.partner_id) : "—"}</span>
            <span className="whitespace-nowrap text-slate-400">{h.at ? fmtDate(h.at) : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AffectDirect = ({ prospectId, busy, setBusy, onSuccess }) => {
  const [partners, setPartners] = useState([]);
  const [selected, setSelected] = useState("");

  useEffect(() => {
    api.get("/admin/partners").then((res) => setPartners(res.data.filter((p) => p.activation_status === "ACTIF"))).catch(() => {});
  }, []);

  const affecter = async () => {
    if (!selected) { toast.error("Sélectionnez un partenaire"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/prospects/${prospectId}/affecter`, { partner_id: selected });
      toast.success("Prospect affecté");
      onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex gap-2">
      <select
        data-testid="prospect-affect-partner-select"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-navy focus:border-cognac focus:outline-none"
      >
        <option value="">— Sélectionner —</option>
        {partners.map((p) => <option key={p.id} value={p.id}>{p.firstname} {p.lastname}</option>)}
      </select>
      <button
        data-testid="prospect-action-affecter"
        disabled={busy}
        onClick={affecter}
        className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50"
      >
        <UserCheck className="h-3.5 w-3.5" /> Affecter
      </button>
    </div>
  );
};

const CreateProspectForm = ({ onCreated }) => {
  const EMPTY = { firstname: "", lastname: "", email: "", phone: "", postal_code: "", city: "", project_type: "", budget: "", message: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstname.trim() || !form.lastname.trim() || !form.phone.trim()) {
      toast.error("Prénom, nom et téléphone sont nécessaires."); return;
    }
    setBusy(true);
    try {
      const res = await api.post("/admin/prospects", form);
      toast.success(res.data._duplicate ? "Personne déjà connue — aucun doublon créé" : "Prospect créé");
      setForm(EMPTY);
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button data-testid="admin-prospect-create-open" onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cognac">
        <UserCheck className="h-3.5 w-3.5" /> Créer un prospect
      </button>
    );
  }
  return (
    <form data-testid="admin-prospect-create-form" onSubmit={submit} className="mt-3 grid w-full gap-2 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-3">
      <input data-testid="admin-prospect-create-firstname" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Prénom *" value={form.firstname} onChange={set("firstname")} />
      <input data-testid="admin-prospect-create-lastname" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Nom *" value={form.lastname} onChange={set("lastname")} />
      <input data-testid="admin-prospect-create-phone" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Téléphone *" value={form.phone} onChange={set("phone")} />
      <input data-testid="admin-prospect-create-email" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Email" value={form.email} onChange={set("email")} />
      <input data-testid="admin-prospect-create-postal" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Code postal" value={form.postal_code} onChange={set("postal_code")} />
      <input data-testid="admin-prospect-create-city" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Commune" value={form.city} onChange={set("city")} />
      <input data-testid="admin-prospect-create-project" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Type de projet" value={form.project_type} onChange={set("project_type")} />
      <input data-testid="admin-prospect-create-budget" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Budget" value={form.budget} onChange={set("budget")} />
      <input data-testid="admin-prospect-create-message" className="rounded-xl border border-slate-200 px-3 py-2 text-sm sm:col-span-3" placeholder="Message / besoin" value={form.message} onChange={set("message")} />
      <div className="flex gap-2 sm:col-span-3">
        <button type="submit" data-testid="admin-prospect-create-submit" disabled={busy} className="rounded-full bg-navy px-4 py-2 text-xs font-bold text-white hover:bg-cognac disabled:opacity-50">Créer</button>
        <button type="button" data-testid="admin-prospect-create-cancel" onClick={() => setOpen(false)} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-500">Annuler</button>
      </div>
    </form>
  );
};

export const AdminProspects = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("UNCOVERED_QUEUE");
  const [detail, setDetail] = useState(null);
  const [neighbors, setNeighbors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [partners, setPartners] = useState([]);
  const [creatingProject, setCreatingProject] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchProspects = useCallback(() => {
    api.get("/admin/prospects")
      .then((res) => setProspects(res.data))
      .catch(() => toast.error("Impossible de charger les prospects"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);
  useEffect(() => { api.get("/admin/partners").then((res) => setPartners(res.data)).catch(() => {}); }, []);

  const openDetail = (p) => {
    setDetail(p);
    setNeighbors([]);
    setCreatedProject(null);
    setConfirmDelete(false);
    if (UNCOVERED_STATUSES.includes(p.routing_status)) {
      api.get(`/admin/prospects/${p.id}/neighbor-partners`).then((res) => setNeighbors(res.data)).catch(() => {});
    }
    if (p.phase3_project_id) {
      api.post(`/admin/prospects/${p.id}/create-client-project`).then((res) => setCreatedProject(res.data.project)).catch(() => {});
    }
  };

  const createClientProject = async () => {
    setCreatingProject(true);
    try {
      const res = await api.post(`/admin/prospects/${detail.id}/create-client-project`);
      setCreatedProject(res.data.project);
      toast.success(res.data.already_exists ? "Projet client déjà existant" : "Projet client créé");
      fetchProspects();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreatingProject(false);
    }
  };

  const act = async (action, partnerId) => {
    setBusy(true);
    try {
      await api.post(`/admin/prospects/${detail.id}/${action}`, partnerId ? { partner_id: partnerId } : {});
      toast.success("Prospect mis à jour");
      setDetail(null);
      fetchProspects();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteProspect = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/prospects/${detail.id}`);
      setProspects((prev) => prev.filter((p) => p.id !== detail.id));
      setConfirmDelete(false);
      setDetail(null);
      toast.success("Demande supprimée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = filter === "UNCOVERED_QUEUE" ? prospects.filter((p) => UNCOVERED_STATUSES.includes(p.routing_status))
    : filter === "SLA_EXPIRED" ? prospects.filter((p) => p.sla_expired)
    : filter === "GAGNE" ? prospects.filter((p) => p.pipeline_stage === "GAGNE" && !p.phase3_project_id)
    : filter === "PERDU" ? prospects.filter((p) => p.pipeline_stage === "PERDU")
    : filter === "CONVERTED" ? prospects.filter((p) => p.phase3_project_id)
    : filter ? prospects.filter((p) => p.routing_status === filter) : prospects;

  const partnerName = (id) => {
    const p = partners.find((x) => x.id === id);
    return p ? `${p.firstname} ${p.lastname}` : null;
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;

  return (
    <div data-testid="admin-prospects">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([label, key]) => (
            <button
              key={label}
              data-testid={`prospect-filter-${key || "tous"}`}
              onClick={() => setFilter(key)}
              className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${
                filter === key ? "border-navy bg-navy text-white" : "border-slate-200 text-slate-500 hover:border-navy"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <CreateProspectForm onCreated={fetchProspects} />
      </div>

      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {filtered.length === 0 ? (
          <p data-testid="admin-prospects-empty" className="py-16 text-center text-sm text-slate-400">Aucun dossier dans cette file.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-4">Reçu le</th>
                  <th className="px-5 py-4">Client</th>
                  <th className="px-5 py-4">Commune</th>
                  <th className="px-5 py-4">Secteur</th>
                  <th className="px-5 py-4">Origine</th>
                  <th className="px-5 py-4">Partenaire d'origine</th>
                  <th className="px-5 py-4">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} data-testid={`prospect-admin-row-${p.id}`} onClick={() => openDetail(p)}
                    className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-cream">
                    <td className="whitespace-nowrap px-5 py-4 text-slate-500">{fmtDate(p.created_at)}</td>
                    <td className="px-5 py-4 font-bold text-navy">{p.firstname} {p.lastname}</td>
                    <td className="px-5 py-4 text-slate-600">{p.city} ({p.postal_code})</td>
                    <td className="px-5 py-4 text-slate-500">{p.territory_code || "—"}</td>
                    <td className="px-5 py-4 text-slate-500">{ORIGIN_LABELS[p.origin_source] || "—"}</td>
                    <td className="px-5 py-4 text-slate-500">{partnerName(p.origin_partner_id) || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${ROUTING_CLS[p.routing_status]}`}>{ROUTING_LABELS[p.routing_status]}</span>
                      {p.phase3_project_id && <span className="ml-1.5 rounded-full bg-sage/20 px-2 py-1 text-[11px] font-bold text-navy">Client</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {detail && (
          <AdminModalOverlay testId="prospect-detail-modal" onClose={() => setDetail(null)}>
            <AdminModalCard>
              <div className="flex shrink-0 items-start justify-between border-b border-slate-100 px-7 py-6 sm:px-9">
                <div>
                  <h2 className="font-display text-2xl font-medium text-navy">{detail.firstname} {detail.lastname}</h2>
                  <p className="mt-1 text-xs text-slate-400">{fmtDate(detail.created_at)}</p>
                </div>
                <button onClick={() => setDetail(null)} data-testid="prospect-detail-close"
                  className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-navy hover:text-white" aria-label="Fermer">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <AdminModalBody>
              <dl className="space-y-2.5 text-sm">
                {[["Email", detail.email || "—"], ["Téléphone", detail.phone], ["Commune", `${detail.city} (${detail.postal_code})`],
                  ["Secteur", detail.territory_code || "Non identifié"], ["Type de projet", detail.project_type || "—"],
                  ["Budget", detail.budget || "—"], ["Origine", ORIGIN_LABELS[detail.origin_source] || "—"],
                  ["Partenaire d'origine", partnerName(detail.origin_partner_id) || "Non encore attribué"],
                  ["Étape commerciale", PIPELINE_LABELS[detail.pipeline_stage] || "—"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2.5">
                    <dt className="text-slate-400">{k}</dt><dd className="text-right font-semibold text-navy">{v}</dd>
                  </div>
                ))}
                {detail.followup_partner_id && (
                  <div data-testid="prospect-followup-info" className="flex justify-between gap-4 border-b border-slate-100 pb-2.5">
                    <dt className="text-slate-400">Partenaire de suivi</dt>
                    <dd className="text-right font-semibold text-navy">{partnerName(detail.followup_partner_id)} ({detail.followup_status})</dd>
                  </div>
                )}
                {detail.sla_deadline && ["WAITING_PARTNER", "PROPOSED_TO_NEIGHBOR"].includes(detail.routing_status) && (
                  <div data-testid="prospect-sla-deadline" className="flex justify-between gap-4 border-b border-slate-100 pb-2.5">
                    <dt className="text-slate-400">Délai partenaire (24h ouvrées)</dt>
                    <dd className="text-right font-semibold text-navy">{fmtDate(detail.sla_deadline)}</dd>
                  </div>
                )}
                {detail.message && <div className="rounded-xl bg-cream p-4 text-slate-600">{detail.message}</div>}
              </dl>

              <p className="mt-4"><span className={`rounded-full px-3 py-1 text-xs font-bold ${ROUTING_CLS[detail.routing_status]}`}>{ROUTING_LABELS[detail.routing_status]}</span></p>

              <ProspectHistoryPanel history={detail.history} partners={partners} />

              {ACCEPTED_STATUSES.includes(detail.routing_status) && (
                <div data-testid="create-client-project-panel" className="mt-5 rounded-2xl border border-slate-200 p-4">
                  {createdProject ? (
                    <div data-testid="client-project-created-banner" className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="flex items-center gap-1.5 text-sm font-bold text-navy"><CheckCircle2 className="h-4 w-4 text-sage" /> Projet client créé</p>
                        <p className="mt-0.5 text-xs text-slate-500">Référence : {createdProject.reference}</p>
                      </div>
                      <button
                        data-testid="copy-client-link"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/espace-client/${createdProject.reference}`);
                          toast.success("Lien copié");
                        }}
                        className="flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cognac"
                      >
                        <Copy className="h-3 w-3" /> Copier le lien
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid="create-client-project-button"
                      disabled={creatingProject}
                      onClick={createClientProject}
                      className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50"
                    >
                      {creatingProject ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderPlus className="h-3.5 w-3.5" />}
                      Créer le projet client
                    </button>
                  )}
                </div>
              )}

              {UNCOVERED_STATUSES.includes(detail.routing_status) && (
                <div className="mt-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Partenaires limitrophes disponibles</p>
                  {neighbors.length === 0 ? (
                    <p className="mt-2 text-sm text-slate-400">Aucun partenaire actif limitrophe pour le moment.</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {neighbors.map((n) => (
                        <div key={n.partner_id} data-testid={`neighbor-partner-${n.partner_id}`} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                          <span className="flex items-center gap-2 text-sm text-navy"><MapPin className="h-3.5 w-3.5 text-cognac" /> {n.partner_name} <span className="text-xs text-slate-400">({n.territory_name})</span></span>
                          <button
                            data-testid={`prospect-propose-${n.partner_id}`}
                            disabled={busy}
                            onClick={() => act("propose", n.partner_id)}
                            className="flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50"
                          >
                            <Send className="h-3 w-3" /> Proposer
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button data-testid="prospect-action-conserver" disabled={busy} onClick={() => act("conserver")}
                      className="flex items-center gap-1.5 rounded-full bg-sky-100 px-4 py-2 text-xs font-bold text-sky-700 transition-colors hover:bg-sky-200 disabled:opacity-50">
                      <ShieldCheck className="h-3.5 w-3.5" /> Conserver à la Centrale
                    </button>
                    <button data-testid="prospect-action-archiver" disabled={busy} onClick={() => act("archiver")}
                      className="flex items-center gap-1.5 rounded-full bg-slate-100 px-4 py-2 text-xs font-bold text-slate-500 transition-colors hover:bg-slate-200 disabled:opacity-50">
                      <Archive className="h-3.5 w-3.5" /> Archiver
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Affecter directement à un partenaire</p>
                <AffectDirect prospectId={detail.id} busy={busy} setBusy={setBusy} onSuccess={() => { setDetail(null); fetchProspects(); }} />
              </div>
              </AdminModalBody>

              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 px-7 py-5 sm:px-9">
                <button
                  data-testid="prospect-detail-close-footer"
                  onClick={() => setDetail(null)}
                  className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:border-navy hover:text-navy"
                >
                  Fermer
                </button>
                <button
                  data-testid="prospect-delete-open"
                  onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-1.5 rounded-full border border-red-200 px-5 py-2.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-600 hover:text-white"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Supprimer la demande
                </button>
              </div>
            </AdminModalCard>
          </AdminModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmDelete && detail && (
          <motion.div
            data-testid="prospect-delete-confirm-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-navy/80 p-5 backdrop-blur-sm"
            onClick={() => !deleting && setConfirmDelete(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-3xl bg-white p-7"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                </span>
                <h3 className="font-display text-xl font-medium text-navy">Supprimer cette demande ?</h3>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-500">
                Cette action supprimera définitivement la demande de <span className="font-semibold text-navy">{detail.firstname} {detail.lastname}</span>. Cette action est irréversible.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  data-testid="prospect-delete-cancel"
                  disabled={deleting}
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:border-navy hover:text-navy disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  data-testid="prospect-delete-confirm"
                  disabled={deleting}
                  onClick={deleteProspect}
                  className="flex items-center gap-1.5 rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Supprimer définitivement
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
